export type Gender = 'W' | 'M' | 'N';
export type Tier = 'Crystal' | 'Silver' | 'Gold';

export interface SourceAttribution {
  channel: string;                    // "fb_post", "ig_bio", "direct" ...
  campaign: string | null;
  variant: string | null;
  referrer: string | null;            // referral_code ของลูกค้าที่แนะนำ
  user_agent: string | null;
  ip: string | null;
  first_seen_at: string;              // ISO timestamp
  registered_at: string;              // ISO timestamp
  // Ad platform click IDs (signup v2)
  fbclid?: string | null;
  gclid?: string | null;
  ttclid?: string | null;
  line_click_id?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

export interface Customer {
  // ─── Identity ─────────────────────────────────
  id: string;                         // = lineUserId (Firestore doc id)
  line_user_id: string;
  line_display_name: string;
  line_picture_url: string | null;

  // ─── LINE-derived profile (จาก LIFF SDK + id_token) ────
  status_message: string | null;      // bio LINE
  language: string | null;            // 'th', 'en', 'ja'
  os: string | null;                  // 'ios' | 'android' | 'web'
  line_friend_status: 'friend' | 'not_friend' | 'unknown' | null;
  line_email: string | null;          // email จริงจาก LINE id_token
  line_email_verified: boolean;       // true ถ้า email มาจาก LINE

  // ─── ข้อมูลที่กรอกตอนสมัคร ────────────────────
  first_name: string;
  last_name: string;
  gender: Gender;
  birthday: string;                   // ISO date YYYY-MM-DD
  phone: string;                      // 10 digits, unique
  email: string | null;               // optional
  province: string;
  district: string | null;            // optional
  postcode: string | null;            // optional

  // ─── Channel attribution (first-touch) ────────
  source: SourceAttribution;

  // ─── Loyalty ──────────────────────────────────
  points: number;
  tier: Tier;
  referral_code: string;              // "DL000123"

  // ─── External sync ────────────────────────────
  shopify_customer_id: string | null;
  shopify_password: string | null;

  // ─── Status ───────────────────────────────────
  is_active?: boolean;                // suspend/enable

  // ─── Audit ────────────────────────────────────
  created_at: string;
  updated_at: string;
  last_seen_at: string;

  // ─── Signup v2 (Line CRM Feedback) ─────────────
  budget_range?: BudgetRange | null;
  signup_value?: number | null;
  shipping?: {
    address1: string | null;
    address2: string | null;
    district: string | null;
    amphur:   string | null;
    province: string | null;
    postcode: string | null;
  };
}

export type BudgetRange =
  | 'under_500'
  | '500_1000'
  | '1000_5000'
  | '5000_10000'
  | '10000_30000'
  | 'anything';

/** UI label + ad-bid value per budget range — single source of truth */
export const BUDGET_OPTIONS: Array<{ value: BudgetRange; label: string; signupValue: number }> = [
  { value: 'under_500',     label: 'ต่ำกว่า 500 บาท',          signupValue: 1 },
  { value: '500_1000',      label: '500 - 1,000 บาท',            signupValue: 50 },
  { value: '1000_5000',     label: '1,000 - 5,000 บาท',          signupValue: 1000 },
  { value: '5000_10000',    label: '5,000 - 10,000 บาท',         signupValue: 5000 },
  { value: '10000_30000',   label: '10,000 - 30,000 บาท',        signupValue: 18000 },
  { value: 'anything',      label: 'เท่าไหร่ก็ได้ขอให้รักษาหาย', signupValue: 25000 },
];

export interface RegisterCustomerInput {
  idToken: string;                    // LINE id_token to verify

  first_name: string;
  last_name: string;
  gender: Gender;
  birthday: string;                   // YYYY-MM-DD
  phone: string;
  email?: string;
  // NEW: budget for treatment (replaces required province)
  budget_range: BudgetRange;
  // Province is now optional — captured later from shipping
  province?: string;
  district?: string;
  postcode?: string;

  source: {
    channel: string;
    campaign?: string;
    variant?: string;
    referrer?: string;
    // NEW: full URL params for paid attribution
    fbclid?: string;
    gclid?: string;
    ttclid?: string;
    line_click_id?: string;
    utm_content?: string;
    utm_term?: string;
  };

  // Optional: extra LINE profile data (เก็บถ้ามี)
  line_profile?: {
    status_message?: string | null;
    language?: string | null;
    os?: string | null;
    friend_status?: 'friend' | 'not_friend' | 'unknown';
  };
}
